<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Product',
  },
})

const router = useRouter()
const { loading, errors, request } = useApiRequest()
const snackbar = ref({ show: false, text: '', color: 'success' })

const products = ref([])
const productsLoading = ref(false)

const form = ref({
  name: '',
  is_required: false,
  is_multiple: false,
  is_active: true,
  product_ids: [],
  modifiers: [
    { name: '', price_adjustment: 0, is_active: true },
  ],
})

// ── Load produk untuk dipilih ────────────────────────────────────────────
const loadProducts = async () => {
  productsLoading.value = true
  try {
    const res = await useApi('/api/v1/products', { query: { per_page: 200, is_active: true } }).get()
    const data = res?.data ?? res
    products.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (err) {
    showSnackbar('Gagal memuat daftar produk', 'error')
  } finally {
    productsLoading.value = false
  }
}

onMounted(loadProducts)

const addModifier = () => {
  form.value.modifiers.push({ name: '', price_adjustment: 0, is_active: true })
}

const removeModifier = idx => {
  if (form.value.modifiers.length <= 1) return
  form.value.modifiers.splice(idx, 1)
}

const submit = async () => {
  const res = await request(() =>
    useApi('/api/v1/modifier-groups').post({ body: form.value })
  )
  
  if (res) {
    showSnackbar('Modifier group berhasil dibuat')
    router.push({ name: 'catalog-modifier-groups' })
  }
}

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const breadcrumbs = [
  { title: 'Katalog', disabled: true },
  { title: 'Modifier Groups', to: { name: 'catalog-modifier-groups' } },
  { title: 'Tambah Group', disabled: true },
]
</script>

<template>
  <div>
    <VBreadcrumbs :items="breadcrumbs" class="px-0 mb-4" />
    <VRow>
      <VCol cols="12" md="8">
        <!-- Group info -->
        <VCard class="mb-4">
          <VCardTitle class="pa-5 pb-0">Informasi Group</VCardTitle>
          <VCardText class="pt-4">
            <VTextField
              v-model="form.name"
              label="Nama Group *"
              placeholder="contoh: Level Es, Topping, Ukuran"
              class="mb-4"
              :error-messages="errors['name']"
            />

            <VRow>
              <VCol cols="12" sm="6">
                <div class="d-flex align-center justify-space-between pa-3 rounded-lg bg-grey-lighten-4">
                  <div>
                    <div class="text-body-2 font-weight-medium">Wajib Dipilih</div>
                    <div class="text-caption text-medium-emphasis">Pelanggan harus memilih salah satu</div>
                  </div>
                  <VSwitch v-model="form.is_required" hide-details color="warning" />
                </div>
              </VCol>
              <VCol cols="12" sm="6">
                <div class="d-flex align-center justify-space-between pa-3 rounded-lg bg-grey-lighten-4">
                  <div>
                    <div class="text-body-2 font-weight-medium">Bisa Multi-pilih</div>
                    <div class="text-caption text-medium-emphasis">Boleh pilih lebih dari satu</div>
                  </div>
                  <VSwitch v-model="form.is_multiple" hide-details color="info" />
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- Produk terkait -->
        <VCard class="mb-4">
          <VCardTitle class="pa-5 pb-0">Terapkan ke Produk</VCardTitle>
          <VCardText class="pt-4">
            <VAutocomplete
              v-model="form.product_ids"
              :items="products"
              :loading="productsLoading"
              item-title="name"
              item-value="id"
              label="Pilih Produk"
              placeholder="Pilih satu atau beberapa produk"
              multiple
              chips
              closable-chips
              hint="Modifier group ini akan muncul di POS saat produk yang dipilih ditambahkan ke cart"
              persistent-hint
              :error-messages="errors['product_ids']"
            />
          </VCardText>
        </VCard>

        <!-- Modifiers -->
        <VCard>
          <VCardTitle class="pa-5 pb-0 d-flex align-center justify-space-between">
            <span>Daftar Opsi (Modifier)</span>
            <VBtn size="small" variant="tonal" prepend-icon="ri-add-line" @click="addModifier">
              Tambah Opsi
            </VBtn>
          </VCardTitle>
          <VCardText class="pt-4">
            <div
              v-for="(modifier, idx) in form.modifiers"
              :key="idx"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-caption text-medium-emphasis">Opsi {{ idx + 1 }}</span>
                <VBtn
                  v-if="form.modifiers.length > 1"
                  icon
                  variant="text"
                  size="x-small"
                  color="error"
                  @click="removeModifier(idx)"
                >
                  <VIcon icon="ri-delete-bin-line" />
                </VBtn>
              </div>

              <VRow dense>
                <VCol cols="12" sm="7">
                  <VTextField
                    v-model="modifier.name"
                    label="Nama Opsi *"
                    placeholder="Extra Shot / Tanpa Gula / Large"
                    :error-messages="errors[`modifiers.${idx}.name`]"
                  />
                </VCol>
                <VCol cols="12" sm="5">
                  <VTextField
                    v-model.number="modifier.price_adjustment"
                    label="Penyesuaian Harga"
                    type="number"
                    prefix="Rp"
                    hint="Bisa negatif"
                    persistent-hint
                    :error-messages="errors[`modifiers.${idx}.price_adjustment`]"
                  />
                </VCol>
              </VRow>

              <VDivider v-if="idx < form.modifiers.length - 1" class="my-3" />
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="4">
        <VCard>
          <VCardTitle class="pa-5 pb-0">Status</VCardTitle>
          <VCardText class="pt-4">
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">Group Aktif</span>
              <VSwitch v-model="form.is_active" hide-details color="success" />
            </div>
            <p class="text-caption text-medium-emphasis mt-1">
              Group non-aktif tidak muncul di POS
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VRow class="mt-2">
      <VCol cols="12" class="d-flex gap-3 justify-end">
        <VBtn variant="tonal" :to="{ name: 'catalog-modifier-groups' }">Batal</VBtn>
        <VBtn :loading="loading" @click="submit">Simpan Group</VBtn>
      </VCol>
    </VRow>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>
